import React from 'react';
import { Info, Layout, Target, ArrowUp, BookOpen } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const ResumeRuleOfThirds = () => {
  return (
    <DashboardLayout>
      <div className="container px-4 md:px-6 py-8">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Resume Rule of Thirds Explained</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Guide Overview</h2>
              <p className="text-gray-700">
                Ensure your resume grabs attention within the critical first glance by applying the Rule of Thirds.
                This simple yet effective technique focuses on structuring your resume for maximum impact.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1 - What is the Rule of Thirds */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">1. What is the Rule of Thirds?</h3>
              <ul className="space-y-4 list-disc">
                <li>The Rule of Thirds divides the first page of your resume into three equal horizontal sections.</li>
                <li>The top third is the most critical since hiring managers typically spend only 6-8 seconds scanning a resume.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 2 - Why It Matters */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Target className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">2. Why It Matters</h3>
              <ul className="space-y-4 list-disc">
                <li>The top third of your resume determines whether a recruiter continues reading.</li>
                <li>It should immediately showcase your most compelling qualifications and key information.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 3 - Optimizing the Top Third */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <ArrowUp className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">3. Optimizing the Top Third</h3>
              <ul className="space-y-4">
                <li><strong>Contact Information:</strong> Ensure your name, professional email, phone number, and LinkedIn URL are clearly visible.</li>
                <li>
                  <strong>Professional Summary:</strong>
                  <ul className="ml-6 mt-2 space-y-2 list-disc">
                    <li>Use 2-3 sentences to highlight your career achievements, skills, and goals.</li>
                    <li>Example: "Experienced Marketing Manager with a proven track record of increasing organic traffic by 40%. Skilled in SEO, PPC, and content strategy."</li>
                  </ul>
                </li>
                <li>
                  <strong>Core Competencies or Key Skills:</strong>
                  <ul className="ml-6 mt-2 space-y-2 list-disc">
                    <li>Use bullet points or a grid format to list 6-10 relevant skills.</li>
                    <li>Example skills:
                      <ul className="ml-6 mt-2 space-y-1 list-disc">
                        <li>Digital Marketing</li>
                        <li>Google Analytics</li>
                        <li>Project Management</li>
                        <li>Budget Optimization</li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 4 - Middle Third Focus */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">4. Middle Third Focus</h3>
              <ul className="space-y-4 list-disc">
                <li>Include your most recent and relevant work experience.</li>
                <li>Use concise bullet points to detail achievements and responsibilities.</li>
                <li>Quantify results wherever possible (e.g., "Increased sales by 25% within one year").</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 5 - Bottom Third Essentials */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">5. Bottom Third Essentials</h3>
              <ul className="space-y-4 list-disc">
                <li>Add education, certifications, and relevant training.</li>
                <li>Include additional sections like languages, volunteer work, or professional affiliations if space allows.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 6 - Tips for Applying */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">6. Tips for Applying the Rule of Thirds</h3>
              <ul className="space-y-4 list-disc">
                <li>Keep formatting consistent and professional.</li>
                <li>Use bold headings and ample white space to guide the reader's eye.</li>
                <li>Avoid cramming too much information into any section.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            By following the Rule of Thirds, you'll ensure your resume is visually appealing, easy to read,
            and highlights your qualifications effectively. Make every second count and stand out from the
            competition!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ResumeRuleOfThirds;